import React, { useState, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import { useNavigate } from 'react-router-dom'; // For navigation to ProductPage

const CategoryTable = ({ categories, onAdd, onEdit, onDelete }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryId, setNewCategoryId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate(); // Add navigate for redirection

  const addButtonRef = useRef(null);
  const editButtonRef = useRef(null);

  // Open Add Modal
  const handleOpenAddModal = () => {
    setNewCategoryName('');
    setNewCategoryId('');
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    if (addButtonRef.current) addButtonRef.current.focus();
  };

  // Open Edit Modal
  const handleOpenEditModal = (category) => {
    setEditCategory(category);
    setNewCategoryName(category.title.translations.en_us);
    setNewCategoryId(category.id);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    if (editButtonRef.current) editButtonRef.current.focus();
  };

  // Add New Category
  const handleAddCategory = () => {
    if (!newCategoryId.trim() || !newCategoryName.trim()) {
      alert('Both Category ID and Name are required!');
      return;
    }

    onAdd({ id: newCategoryId, title: newCategoryName });
    handleCloseAddModal();
  };

  // Edit Existing Category
  const handleEditCategory = () => {
    if (!newCategoryName.trim()) {
      alert('Category name is required!');
      return;
    }

    const updatedCategory = {
      id: editCategory.id,
      title: { translations: { en_us: newCategoryName } },
    };

    onEdit(updatedCategory);
    handleCloseEditModal();
  };

  const filteredCategories = categories.filter(
    (category) =>
      category.title.translations.en_us.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <Typography variant="h5" style={{ marginBottom: '16px' }}>
        Categories List
      </Typography>
      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: '16px' }}>
        <TextField
          label="Search Categories"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flexGrow: 1, marginRight: '16px' }}
        />
        <Button
          ref={addButtonRef}
          variant="contained"
          color="primary"
          onClick={handleOpenAddModal}
        >
          Add Category
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Category ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCategories.map((category) => (
              <TableRow key={category.id}>
                <TableCell>{category.id}</TableCell>
                <TableCell>{category.title.translations.en_us}</TableCell>
                <TableCell>
                  <Button
                    ref={editButtonRef}
                    variant="contained"
                    color="primary"
                    onClick={() => handleOpenEditModal(category)}
                    style={{ marginRight: '8px' }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => onDelete(category.id)}
                    style={{ marginRight: '8px' }}
                  >
                    Delete
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => navigate(`/products/${category.id}`)} // Redirect to ProductPage
                  >
                    Products
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filteredCategories.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} style={{ textAlign: 'center' }}>
                  No matching categories found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Modal */}
      <Dialog open={isAddModalOpen} onClose={handleCloseAddModal}>
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category ID"
            type="text"
            fullWidth
            value={newCategoryId}
            onChange={(e) => setNewCategoryId(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Category Name"
            type="text"
            fullWidth
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddModal} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddCategory} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onClose={handleCloseEditModal}>
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            type="text"
            fullWidth
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleEditCategory} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CategoryTable;
